import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';
import { getVoiceAttributes } from './voice';
import { interpolateVariables } from './interpolate';

export type GetLanguagePreferenceConfig = {
  mode: 'ask' | 'set';
  prompt: string;
  outputVariable: string;
  timeout: number;
  retryCount: number;
  retryPrompt: string;
  setLanguage: string;
  digitByLanguage: Record<string, string>;
};

function retryKeyFor(nodeId: string): string {
  return `_langRetries_${nodeId}`;
}

const FALLBACK_LANGUAGE_CODE = 'en';

function resolveSelectedLanguageCode(
  digit: string,
  digitByLanguage: Record<string, string> | undefined,
  languages: ReadonlyArray<{ code: string }>,
): string | undefined {
  const hasExplicitMapping = digitByLanguage && Object.keys(digitByLanguage).length > 0;

  if (hasExplicitMapping) {
    const match = Object.entries(digitByLanguage).find(([, d]) => d === digit);
    if (!match) return undefined;
    const code = match[0];
    return languages.some((l) => l.code === code) ? code : undefined;
  }

  const digitIndex = parseInt(digit, 10) - 1;
  if (digitIndex >= 0) return languages[digitIndex]?.code;

  return undefined;
}

function generateTeXML(config: GetLanguagePreferenceConfig, context: TeXMLContext): TeXMLElement[] {
  if (config.mode === 'set') {
    return [];
  }

  const callbackUrl = buildCallbackUrl(context);
  const retryKey = retryKeyFor(context.nodeId);
  const retryCount = parseInt(context.variables[retryKey] || '0', 10);
  const template = retryCount > 0 && config.retryPrompt ? config.retryPrompt : config.prompt;
  const prompt = interpolateVariables(template, context.variables, context.speechFormats);

  return [
    {
      tag: 'Gather',
      attributes: {
        input: 'dtmf',
        numDigits: '1',
        timeout: config.timeout.toString(),
        action: callbackUrl,
        method: 'POST',
      },
      children: prompt
        ? [
            {
              tag: 'Say',
              attributes: getVoiceAttributes(context.voiceConfig, undefined, undefined, context.elevenLabsApiKeyRef),
              content: prompt,
            },
          ]
        : undefined,
    },
    {
      tag: 'Redirect',
      attributes: { method: 'POST' },
      content: callbackUrl,
    },
  ];
}

function buildRetryElements(
  config: GetLanguagePreferenceConfig,
  context: TeXMLContext,
  newRetryCount: number,
): CallbackResult {
  const callbackUrl = buildCallbackUrl(context);
  const template = config.retryPrompt || config.prompt;
  const prompt = interpolateVariables(template, context.variables, context.speechFormats);

  const retryKey = retryKeyFor(context.nodeId);

  return {
    variables: { [retryKey]: String(newRetryCount) },
    elements: [
      {
        tag: 'Gather',
        attributes: {
          input: 'dtmf',
          numDigits: '1',
          timeout: config.timeout.toString(),
          action: callbackUrl,
          method: 'POST',
        },
        children: [
          {
            tag: 'Say',
            attributes: getVoiceAttributes(context.voiceConfig, undefined, undefined, context.elevenLabsApiKeyRef),
            content: prompt,
          },
        ],
      },
      {
        tag: 'Redirect',
        attributes: { method: 'POST' },
        content: callbackUrl,
      },
    ],
  };
}

export function getLanguagePreferenceCallbackHandler(
  event: TelnyxEvent,
  config: GetLanguagePreferenceConfig,
  variables: Record<string, string>,
  context: TeXMLContext,
): CallbackResult {
  const digits = event.Digits;
  const maxRetries = config.retryCount ?? 0;
  const retryKey = retryKeyFor(context.nodeId);
  const currentRetries = parseInt(variables[retryKey] || '0', 10);
  const varName = config.outputVariable || '_language';
  const fallbackLanguage = context.languageConfig?.defaultLanguage || FALLBACK_LANGUAGE_CODE;

  if (!digits) {
    if (currentRetries < maxRetries) {
      return buildRetryElements(config, context, currentRetries + 1);
    }
    return { nextHandleId: 'out', variables: { [varName]: fallbackLanguage, [retryKey]: '0' } };
  }

  const languages = context.languageConfig?.languages ?? [];
  const selectedCode = resolveSelectedLanguageCode(digits, config.digitByLanguage, languages);

  if (selectedCode) {
    return {
      nextHandleId: 'out',
      variables: { [varName]: selectedCode, [retryKey]: '0' },
    };
  }

  if (currentRetries < maxRetries) {
    return buildRetryElements(config, context, currentRetries + 1);
  }

  return { nextHandleId: 'out', variables: { [varName]: fallbackLanguage, [retryKey]: '0' } };
}

function getSetModeVariables(config: GetLanguagePreferenceConfig): Record<string, string> | undefined {
  if (config.mode !== 'set') return undefined;
  const varName = config.outputVariable || '_language';
  return { [varName]: config.setLanguage || FALLBACK_LANGUAGE_CODE };
}

function validateConfig(config: GetLanguagePreferenceConfig): ValidationResult {
  const errors: string[] = [];

  if (config.mode === 'ask') {
    if (!config.prompt.trim()) {
      errors.push('Prompt is required');
    }
    if (config.timeout < 1 || config.timeout > 30) {
      errors.push('Timeout must be between 1 and 30 seconds');
    }
    if (config.retryCount < 0 || config.retryCount > 5) {
      errors.push('Retry count must be between 0 and 5');
    }

    const digits = Object.values(config.digitByLanguage ?? {}).filter((d) => d.length > 0);
    const unique = new Set(digits);
    if (digits.length !== unique.size) {
      errors.push('Each language must use a different digit');
    }
  }

  return { valid: errors.length === 0, errors };
}

export const getLanguagePreferenceWidgetCore: WidgetCoreDefinition<GetLanguagePreferenceConfig> = {
  id: 'getLanguagePreference',
  version: 1,
  name: 'Get Language Preference',
  description: 'Ask the caller to select their preferred language or set it directly',
  category: 'input',
  tier: 'standard',

  defaultConfig: {
    mode: 'ask',
    prompt: '',
    outputVariable: '_language',
    timeout: 5,
    retryCount: 1,
    retryPrompt: '',
    setLanguage: '',
    digitByLanguage: {},
  },

  variables: {
    outputs: [
      {
        configKey: 'outputVariable',
        description: 'The selected language code',
        type: 'string',
        availableOnHandleTypes: ['default'],
      },
    ],
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: (config: GetLanguagePreferenceConfig) => config.mode === 'ask',
    resolveVariables: (config: GetLanguagePreferenceConfig) => getSetModeVariables(config),
  },

  validation: {
    validateConfig,
  },
};

export { getSetModeVariables };
